import { Box, CircularProgress, Icon, Typography } from '@mui/material'
import AdxTableComponent from 'components/reusable/table/AdxTableComponent'
import RecruitmentPagination from 'module/recruitment/reusable/RecruitmentPagination'
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { getOrderHistory } from 'services/api/modules/recruitment/order'
import TransactionDetailDrawer from './TransactionDetailDrawer'
import AdxEmptyState from 'components/reusable/emptyState/AdxEmptyState'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const TransactionHistory = ({}) => {
  //pagination
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // API CALL
  const queryClient = useQueryClient()
  useEffect(() => {
    queryClient.invalidateQueries('getOrderHistory')
    queryClient.resetQueries('getOrderHistory')
  }, [])

  const {
    isLoading: orderIsLoading,
    error: orderError,
    data: orderData,
  } = useQuery(
    ['getOrderHistory', rowsPerPage, page],
    async () => {
      const { data } = await getOrderHistory(rowsPerPage, page + 1)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const tableColumn = [
    {
      id: 1,
      Header: 'Date',
      accessor: 'updated_at',
      Cell: (props) => {
        const { value } = props
        const dt = new Date(value)
        const formattedDate = dt.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })

        //check of the time is in PM and the hour clock is 00, change it 12
        const [dateString, time] = formattedDate.split(', ')
        const [hour, minuteTemp] = time.split(':')
        const [minute, ampm] = minuteTemp.split(' ')
        const convertTime = (ampm, dateString, minuteTemp, hour, formattedDate) => {
          if (ampm === 'pm' && hour === '00') {
            return `${dateString}, 12:${minuteTemp}`
          } else {
            return formattedDate
          }
        }
        const newDate = convertTime(ampm, dateString, minuteTemp, hour, formattedDate)

        return <Typography>{newDate}</Typography>
      },
    },
    {
      id: 2,
      Header: 'Transaction',
      accessor: 'order_type',
      Cell: (props) => {
        const { value } = props
        if (value === 'candidate_purchase') {
          return <Typography>Candidate purchase</Typography>
        } else if (value === 'credit_purchase') {
          return <Typography>Credit topup</Typography>
        }
      },
    },
    {
      id: 3,
      Header: 'Status',
      accessor: 'order_status',
      Cell: (props) => {
        const { value } = props
        if (value === 'Payment successful') {
          return <Typography>Successful</Typography>
        } else {
          return <Typography color="error.main">Failed</Typography>
        }
      },
    },
    {
      id: 4,
      Header: 'Amount',
      accessor: 'amount',
      Cell: (props) => {
        const { value } = props

        if (props?.row?.original?.order_type === 'candidate_purchase') {
          return <Typography>-RM {value}.00</Typography>
        } else if (props?.row?.original?.order_type === 'credit_purchase') {
          return <Typography>+RM {value}.00</Typography>
        }
      },
    },
  ]

  //custom  empty state
  const TransactionHistoryEmpty = ({ icon = 'question_mark', message = 'No data' }) => (
    <Box
      height={200}
      display="flex"
      justifyContent="center"
      alignItems="center"
      bgcolor="gray-background.main"
      width={1}
      borderRadius={3}
      boxSizing="border-box"
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        rowGap={1}
      >
        <Icon sx={{ fontSize: 30, color: 'light-gray-text.main' }}>{icon}</Icon>
        <Typography align="center" variant="body1" fontWeight="bold" color="light-gray-text.main">
          {message}
        </Typography>
      </Box>
    </Box>
  )
  // drawer logic
  const [drawerState, setDrawerState] = useState({ right: false })
  const [data, setData] = useState([])
  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    setData(event)
  }
  if (orderIsLoading) {
    return (
      <Box>
        <Typography variant="h4" color="dark-text.main">
          Transaction History
        </Typography>

        <Box display="flex" flexDirection="column" gap={2} mt={12} alignItems="center">
          <CircularProgress color="alternate" />
        </Box>
      </Box>
    )
  } else if (orderError !== null || orderData === undefined || typeof orderData !== 'object') {
    return (
      <Box>
        <Typography variant="h4" color="dark-text.main">
          Transaction History
        </Typography>

        <Box display="flex" flexDirection="column" mt={2} alignItems="center">
          <AdxTableComponent
            column={tableColumn}
            data={orderData?.data ?? []}
            nav="View"
            onClick={handleDrawer('right', true)}
            sxHeader
            sxNav
            sxCell
            sx={{ borderRadius: 0 }}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            bgcolor="white.main"
            mb={6}
            sx={{ width: '100%' }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                width: '100%',
                height: 200,
                overflow: 'hidden',
              }}
              borderTop={1}
              borderColor={'gray-line.main'}
            >
              <AdxEmptyStateV2
                icon={<Icon sx={{ fontSize: 40 }}>error</Icon>}
                message="No transactions found"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    )
  } else {
    return (
      <Box>
        <Typography variant="h4" color="dark-text.main">
          Transaction History
        </Typography>

        <Box display="flex" flexDirection="column" mt={2} alignItems="center">
          <AdxTableComponent
            column={tableColumn}
            data={orderData?.data ?? []}
            nav="View"
            onClick={handleDrawer('right', true)}
            sxHeader
            sxNav
            sxCell
            sx={{ borderRadius: 0 }}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            bgcolor="white.main"
            mb={6}
            sx={{ width: '100%' }}
          >
            <RecruitmentPagination
              count={orderData?.total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
        <TransactionDetailDrawer
          data={data}
          drawerState={drawerState}
          handleDrawer={handleDrawer}
          isLoading={orderIsLoading}
        />
      </Box>
    )
  }
}
export default TransactionHistory
